import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { LOCALES, resolve } from "./translations";

const LanguageCtx = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  dir: "ltr",
  locales: LOCALES,
  meta: LOCALES[0],
});

const STORAGE_KEY = "hupla-lang";

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && LOCALES.some((l) => l.code === saved)) return saved;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[i18n] localStorage read failed", err);
      }
    }
    return "en";
  });

  const meta = useMemo(() => LOCALES.find((l) => l.code === lang) || LOCALES[0], [lang]);
  const dir = meta.dir;

  const setLang = useCallback((code) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[i18n] localStorage write failed", err);
      }
    }
  }, []);

  const t = useCallback((key, vars) => {
    let out = resolve(lang, key);
    if (typeof out !== "string") return out;
    if (vars) {
      out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
    }
    return out;
  }, [lang]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
  }, [lang, dir]);

  const value = useMemo(() => ({ lang, setLang, t, dir, locales: LOCALES, meta }), [lang, setLang, t, dir, meta]);
  return <LanguageCtx.Provider value={value}>{children}</LanguageCtx.Provider>;
}

export function useT() {
  return useContext(LanguageCtx);
}
